import * as JSONMapper from "json-mapper";
import {JSONMapped, jsonProperty} from "json-mapper-reflect";
import {contextPath} from "site";
import {Device} from "./device";
import {DeviceModel} from "./device-model";
import {GPAircraft} from "./gp-aircraft";
import {MasterAircraftWakeupSettings} from "./master-aircraft-wakeup-settings";
import {AircraftPermissions} from "./aircraft-permissions";

export class Aircraft extends JSONMapped() {
	@jsonProperty()
	uuid: string;

	@jsonProperty()
	masterAircraftUuid: string;

	@jsonProperty()
	id: string;

	@jsonProperty()
	name: string;

	@jsonProperty()
	year: number;

	@jsonProperty()
	serial: string;

	@jsonProperty()
	aircraftMakeName: string;

	@jsonProperty()
	aircraftMakeId: number;

	@jsonProperty()
	aircraftModelName: string;

	@jsonProperty()
	aircraftModelId: number;

	@jsonProperty()
	isRotorcraft: boolean;

	@jsonProperty()
	hasPhoto: boolean;

	@jsonProperty()
	imageUrl: string;

	@jsonProperty()
	avdbStatus: string;

	@jsonProperty(JSONMapper.timestampFromISO)
	nextInstallAt: string;

	@jsonProperty(JSONMapper.timestampFromISO)
	recentInstalledAt: string;

	@jsonProperty(JSONMapper.timestampFromISO)
	mostRecentInstallFailureDateTime: string;

	@jsonProperty(JSONMapper.arrayOfClass(Device))
	devices: Array<Device>;

	@jsonProperty(JSONMapper.arrayOfClass(DeviceModel))
	legacyConnextDevices: Array<DeviceModel>;

	@jsonProperty(JSONMapper.arrayOfClass(DeviceModel))
	connectedCockpitDevices: Array<DeviceModel>;

	@jsonProperty()
	connectedCockpitDeviceAvdbStatus: string;

	@jsonProperty()
	doesNotOwnCurrentCycle: boolean;

	@jsonProperty(JSONMapper.classByMethods(GPAircraft))
	planningProfile: GPAircraft;

	@jsonProperty()
	belongsToCurrentUser: boolean;

	@jsonProperty()
	jeppTierId: number;

	@jsonProperty()
	customerGuid: string;

	@jsonProperty()
	organizationUuid: string;

	@jsonProperty()
	isMigrated: boolean;

	@jsonProperty()
	aircraftPlaceholderImageUrl: string;

	@jsonProperty()
	isConnectedCockpitEnabled: boolean;

	@jsonProperty()
	mostRecentInstallFailure: string;

	@jsonProperty(JSONMapper.classByMethods(AircraftPermissions))
	permissions: AircraftPermissions;

	@jsonProperty(JSONMapper.classByMethods(MasterAircraftWakeupSettings))
	wakeupSettings: MasterAircraftWakeupSettings;

	@jsonProperty()
	activeTransferExists: boolean;

	@jsonProperty()
	ownerCustomerGuid: string;

	constructor(data?: Partial<Aircraft>) {
		super();
		Object.assign(this, data);
	}

	makeModelYear(): string {
		// modelName + seriesName are now the same -- we only need to display one of them
		return `${this.year} ${this.aircraftMakeName} ${this.aircraftModelName}`;
	}

	// managedCustomerGuid is for when the logged-in user is a dealer who is managing a customer's account.
	// if the logged-in user is not a dealer, managedCustomerGuid should not be provided
	photoURL(managedCustomerGuid: string, roleInviteUuid: string): string {
		// if the aircraft has no custom image, have the front-end query the cdn directly
		// if it does have a custom image, retrieve it through aircraft management

		if (!this.hasPhoto) {
			// get directly from cdn
			return this.imageUrl;
		}

		if (managedCustomerGuid) {
			this.verifyUuid(managedCustomerGuid);
		}

		if (roleInviteUuid) {
			this.verifyUuid(roleInviteUuid);
		}

		let param = roleInviteUuid ? `?roleInviteUuid=${roleInviteUuid}` : managedCustomerGuid ? `?managedCustomerGuid=${managedCustomerGuid}` : "";
		return `${contextPath}/aircraft/${this.uuid}/image.png${param}`;
	}

	get isGPAircraft(): boolean {
		return this.planningProfile != null;
	}

	get isFlygarminAircraft(): boolean {
		return this.aircraftModelId !== 0;
	}

	private verifyUuid(uuid: string) {
		if (!(/^(([0-9]+)|([0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}))$/.test(uuid))) {
			throw new Error("The provided uuid is invalid");
		}
	}
}
